import React from "react";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import { Button } from "@mui/material";
import NotificationImportantOutlinedIcon from '@mui/icons-material/NotificationImportantOutlined';

import { ScheduleCustomPermissionStatusesEnum } from "../../../constants";
import "./PublicationNotice.scss";

export interface PublicationNoticeProps {
    customPermissionBannerData?: any;
    title?: string;
    scheduledDate?: string;
    bodyPrefix?: string;
    actionText?: string;
    onAction?: () => void;
    variant?: ScheduleCustomPermissionStatusesEnum | string;
    icon?: React.ReactNode;
    showButton?: boolean;
    buttonLabel?: string;
    className?: string;
    ariaLabel?: string;
    actionHref?: string;
}

function getVariantIcon(variant: ScheduleCustomPermissionStatusesEnum | string): React.ReactNode | null {
    switch (variant) {
        case ScheduleCustomPermissionStatusesEnum.NotScheduled:
        case ScheduleCustomPermissionStatusesEnum.Cancelled:
            return <InfoOutlinedIcon className="pubNotice__icon-svg pubNotice__icon-svg--warning" />;
        case ScheduleCustomPermissionStatusesEnum.Scheduled:
            return <EventAvailableOutlinedIcon className="pubNotice__icon-svg pubNotice__icon-svg--warning" />;
        case ScheduleCustomPermissionStatusesEnum.VerificationInProgress:
            return <InfoOutlinedIcon className="pubNotice__icon-svg pubNotice__icon-svg--warning" />;
        case ScheduleCustomPermissionStatusesEnum.InProgress:
            return <NotificationImportantOutlinedIcon className="pubNotice__icon-svg pubNotice__icon-svg--muted" />;
        case ScheduleCustomPermissionStatusesEnum.Verified:
            return <CheckCircleOutlinedIcon className="pubNotice__icon-svg pubNotice__icon-svg--success" />;
        default:
            return null;
    }
}

const PublicationNoticeVariantToClass: Record<ScheduleCustomPermissionStatusesEnum | string, string> = {
    [ScheduleCustomPermissionStatusesEnum.NotScheduled]: "notPublication",
    [ScheduleCustomPermissionStatusesEnum.Cancelled]: "notPublication",
    [ScheduleCustomPermissionStatusesEnum.Scheduled]: "publication",
    [ScheduleCustomPermissionStatusesEnum.VerificationInProgress]: "verificationNeeded",
    [ScheduleCustomPermissionStatusesEnum.InProgress]: "verificationInProgress",
    [ScheduleCustomPermissionStatusesEnum.Verified]: "verificationApproved",
};


export const PublicationNotice: React.FC<PublicationNoticeProps> = ({
    customPermissionBannerData,
    title,
    scheduledDate,
    bodyPrefix,
    variant = ScheduleCustomPermissionStatusesEnum.NotScheduled,
    className,
}) => {
    const suffix = PublicationNoticeVariantToClass[variant];
    const classes = ["pubNotice", `pubNotice--${suffix}`, className]
        .filter(Boolean)
        .join(" ");

    const copy: any = customPermissionBannerData[variant];
    const resolvedTitle = title ?? copy?.title;

    const resolvedBodyPrefix =
        (bodyPrefix ?? copy?.bodyPrefix)?.replace("{DATE}", scheduledDate ?? "");

    const resolvedActionText = copy?.actionText ? copy?.actionText : "";
    const resolvedActionButtonText = copy?.actionButtonText ? copy?.actionButtonText : "";
    const resolvedIcon = getVariantIcon(variant);

    const ActionInline = () => {
        if (!copy?.actionTextAction) {
            return (
                <span className="pubNotice__normalText">
                    {resolvedActionText}
                </span>
            );
        }       
        return (
            <button type="button" className="pubNotice__linkButton" onClick={copy?.actionTextAction}>
                {resolvedActionText}
            </button>
        );
    };

    return (
        <section className={classes} role="status">
            <div className="pubNotice__icon" aria-hidden="true">
                {resolvedIcon}
            </div>

            <div className="pubNotice__content">
                <div className="pubNotice__header">
                    <h3 className="pubNotice__title">{resolvedTitle}</h3>
                </div>
                <p className="pubNotice__text">
                    {resolvedBodyPrefix}
                    {resolvedActionText && (
                        <>
                            {" "}
                            <ActionInline />
                        </>
                    )}
                </p>

                {variant === ScheduleCustomPermissionStatusesEnum.VerificationInProgress && (
                    <Button
                        variant="outlined"
                        className="pubNotice__actionBtn"
                        size="medium"
                        onClick={copy?.actionTextAction}
                    >
                        {resolvedActionButtonText}
                    </Button>
                )}
            </div>
        </section>
    );
};

export default PublicationNotice;